import { useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Calendar, Filter, Play } from "lucide-react";
import { today, daysAgo } from "@/lib/api";

/**
 * Compute a from/to date pair for a Zoho-style preset.
 * Returns ISO date strings.
 */
export function rangeForPreset(preset, customFrom, customTo) {
    const t = new Date(today() + "T00:00:00");
    const fmt = (d) => d.toISOString().slice(0, 10);
    const startOf = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
    const endOf = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
    let from = t, to = t;
    switch (preset) {
        case "today": from = to = t; break;
        case "yesterday": from = to = startOf(new Date(t.getTime() - 86400000)); break;
        case "this_week": {
            const s = new Date(t); s.setDate(t.getDate() - t.getDay()); from = startOf(s); to = endOf(t); break;
        }
        case "this_month": from = new Date(t.getFullYear(), t.getMonth(), 1); to = endOf(t); break;
        case "previous_month": {
            from = new Date(t.getFullYear(), t.getMonth() - 1, 1);
            to = new Date(t.getFullYear(), t.getMonth(), 0); break;
        }
        case "this_quarter": {
            const q = Math.floor(t.getMonth() / 3);
            from = new Date(t.getFullYear(), q * 3, 1);
            to = endOf(t); break;
        }
        case "previous_quarter": {
            const q = Math.floor(t.getMonth() / 3) - 1;
            const year = q < 0 ? t.getFullYear() - 1 : t.getFullYear();
            const qq = (q + 4) % 4;
            from = new Date(year, qq * 3, 1);
            to = new Date(year, qq * 3 + 3, 0); break;
        }
        case "this_year": {
            // Indian FY = Apr-Mar
            const y = t.getMonth() < 3 ? t.getFullYear() - 1 : t.getFullYear();
            from = new Date(y, 3, 1); to = new Date(y + 1, 2, 31); break;
        }
        case "previous_year": {
            const y = t.getMonth() < 3 ? t.getFullYear() - 2 : t.getFullYear() - 1;
            from = new Date(y, 3, 1); to = new Date(y + 1, 2, 31); break;
        }
        case "last_30": from = new Date(t.getTime() - 29 * 86400000); to = endOf(t); break;
        case "last_180": from = new Date(t.getTime() - 179 * 86400000); to = endOf(t); break;
        case "custom": return { from: customFrom || daysAgo(180), to: customTo || today() };
        default: from = new Date(t.getTime() - 179 * 86400000); to = endOf(t);
    }
    return { from: fmt(from), to: fmt(to) };
}

const PRESETS = [
    { v: "today", l: "Today" },
    { v: "yesterday", l: "Yesterday" },
    { v: "this_week", l: "This Week" },
    { v: "this_month", l: "This Month" },
    { v: "previous_month", l: "Previous Month" },
    { v: "this_quarter", l: "This Quarter" },
    { v: "previous_quarter", l: "Previous Quarter" },
    { v: "this_year", l: "This Year (Apr-Mar)" },
    { v: "previous_year", l: "Previous Year" },
    { v: "last_30", l: "Last 30 days" },
    { v: "last_180", l: "Last 6 months" },
    { v: "custom", l: "Custom Range" },
];

/**
 * Zoho-style report filter bar.
 * Props:
 *   preset, onPresetChange
 *   customFrom, customTo, onCustomFromChange, onCustomToChange
 *   basis ("accrual"|"cash"), onBasisChange
 *   showBasis (bool), showRunButton (bool)
 *   onRun() → caller refreshes its data
 */
export default function ReportFilters({
    preset, onPresetChange,
    customFrom, customTo, onCustomFromChange, onCustomToChange,
    basis = "accrual", onBasisChange,
    showBasis = true, showRunButton = true, onRun,
    rightSlot = null,
}) {
    const isCustom = preset === "custom";
    const { from, to } = useMemo(() => rangeForPreset(preset, customFrom, customTo), [preset, customFrom, customTo]);

    return (
        <div className="bg-card border border-border rounded-md p-4 mb-4 flex flex-wrap items-end gap-3" data-testid="report-filters">
            <div className="flex flex-col gap-1.5">
                <Label className="text-[10px] uppercase tracking-[0.12em] text-muted-foreground font-semibold">Date range</Label>
                <Select value={preset} onValueChange={onPresetChange}>
                    <SelectTrigger className="h-9 w-[180px]" data-testid="filter-preset">
                        <Calendar className="w-3.5 h-3.5 mr-1.5 text-muted-foreground" />
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        {PRESETS.map((p) => <SelectItem key={p.v} value={p.v}>{p.l}</SelectItem>)}
                    </SelectContent>
                </Select>
            </div>
            {isCustom && (
                <>
                    <div className="flex flex-col gap-1.5">
                        <Label className="text-[10px] uppercase tracking-[0.12em] text-muted-foreground font-semibold">From</Label>
                        <Input type="date" value={customFrom} onChange={(e) => onCustomFromChange(e.target.value)} className="h-9 w-[140px]" data-testid="filter-from" />
                    </div>
                    <div className="flex flex-col gap-1.5">
                        <Label className="text-[10px] uppercase tracking-[0.12em] text-muted-foreground font-semibold">To</Label>
                        <Input type="date" value={customTo} onChange={(e) => onCustomToChange(e.target.value)} className="h-9 w-[140px]" data-testid="filter-to" />
                    </div>
                </>
            )}
            {!isCustom && (
                <div className="flex flex-col gap-1.5 text-[11.5px] text-muted-foreground self-end pb-1.5">
                    {from} → {to}
                </div>
            )}
            {showBasis && (
                <div className="flex flex-col gap-1.5">
                    <Label className="text-[10px] uppercase tracking-[0.12em] text-muted-foreground font-semibold">Report basis</Label>
                    <Select value={basis} onValueChange={onBasisChange}>
                        <SelectTrigger className="h-9 w-[140px]" data-testid="filter-basis"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="accrual">Accrual</SelectItem>
                            <SelectItem value="cash">Cash</SelectItem>
                        </SelectContent>
                    </Select>
                </div>
            )}
            <div className="ml-auto flex items-center gap-2">
                {rightSlot}
                {showRunButton && onRun && (
                    <Button size="sm" onClick={onRun} data-testid="filter-run">
                        <Play className="w-3.5 h-3.5 mr-1.5" />Run Report
                    </Button>
                )}
            </div>
        </div>
    );
}
